import type { Interrupt } from '@ag-ui/client';
import is from '@sindresorhus/is';

/** The longest delay setTimeout honours; a later expiry is checked again after it. */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

const expiryOf = ({ expiresAt }: Interrupt): number => (is.undefined(expiresAt) ? Number.NaN : Date.parse(expiresAt));

/**
 * Calls `onExpiry` once the next of the tracked interrupts expires, so its question turns read-only
 * without another event. Tracking the same list again keeps the running timer.
 */
export class InterruptExpiryTimer {
    private readonly onExpiry: () => void;
    private timer: ReturnType<typeof setTimeout> | null = null;
    private tracked: readonly Interrupt[] | null = null;

    constructor(onExpiry: () => void) {
        this.onExpiry = onExpiry;
    }

    track(interrupts: readonly Interrupt[]): void {
        if (interrupts === this.tracked) return;
        this.tracked = interrupts;
        this.dispose();
        const now = Date.now();
        const nextExpiry = Math.min(...interrupts.map(expiryOf).filter((at) => at > now));
        if (!Number.isFinite(nextExpiry)) return;
        this.timer = setTimeout(
            () => {
                this.timer = null;
                this.tracked = null;
                this.onExpiry();
            },
            Math.min(nextExpiry - now, MAX_TIMER_DELAY_MS),
        );
    }

    dispose(): void {
        if (is.null(this.timer)) return;
        clearTimeout(this.timer);
        this.timer = null;
    }
}
